// POS API
import { api, apiGet, apiPost, apiPatch } from './client'

export const posApi = {
  // Cart
  getCart: () =>
    apiGet<Cart>('/pos/cart'),

  addToCart: (data: CartItemAdd) =>
    apiPost<Cart>('/pos/cart/items', data),

  updateCartItem: (itemId: string, data: CartItemUpdate) =>
    apiPatch<Cart>(`/pos/cart/items/${itemId}`, data),

  removeFromCart: (itemId: string) =>
    api.delete(`/pos/cart/items/${itemId}`).then(res => res.data),

  clearCart: () =>
    api.delete('/pos/cart').then(res => res.data),

  // Sales
  listSales: (params?: SaleListParams) =>
    apiGet<PaginatedResponse<Sale>>('/pos/sales', params),

  getSale: (id: string) =>
    apiGet<Sale>(`/pos/sales/${id}`),

  createSale: (data: SaleCreate) =>
    apiPost<Sale>('/pos/sales', data),

  voidSale: (id: string, reason: string) =>
    apiPost<Sale>(`/pos/sales/${id}/void`, { reason }),

  // Payments
  addPayment: (saleId: string, data: PaymentCreate) =>
    apiPost<Payment>(`/pos/sales/${saleId}/payments`, data),

  initiateZarinpal: (saleId: string) =>
    apiPost<{ payment_url: string; authority: string }>(`/pos/payments/zarinpal/initiate`, { sale_id: saleId }),

  // Receipts
  getReceipt: (saleId: string, format: 'json' | 'html' | 'pdf' | 'text' = 'json') =>
    apiGet<Receipt>(`/pos/sales/${saleId}/receipt`, { format }),

  // Shifts
  listShifts: (params?: ShiftListParams) =>
    apiGet<PaginatedResponse<Shift>>('/pos/shifts', params),

  getCurrentShift: () =>
    apiGet<Shift | null>('/pos/shifts/current'),

  openShift: (data: ShiftOpen) =>
    apiPost<Shift>('/pos/shifts/open', data),

  closeShift: (shiftId: string, data: ShiftClose) =>
    apiPost<Shift>(`/pos/shifts/${shiftId}/close`, data),
}

// Types
export interface PaginationParams {
  page?: number
  page_size?: number
  search?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface Cart {
  id: string
  user_id: string
  customer_id?: string
  status: 'active' | 'completed' | 'abandoned' | 'expired'
  items: CartItem[]
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  created_at: string
  updated_at: string
  completed_at?: string
}

export interface CartItem {
  id: string
  product_id: string
  variant_id?: string
  quantity: number
  unit_price: number
  discount_percent: number
  tax_rate: number
  product: { id: string; sku: string; name: string; sale_price: number; tax_rate: number; images: string[] }
  variant?: { id: string; sku: string; name: string; sale_price: number; images: string[] }
  line_total: number
  discount_amount: number
  tax_amount: number
}

export interface CartItemAdd {
  product_id: string
  variant_id?: string
  quantity: number
  discount_percent?: number
}

export interface CartItemUpdate {
  quantity: number
  discount_percent?: number
}

export interface Sale {
  id: string
  sale_number: string
  user_id: string
  warehouse_id: string
  customer_id?: string
  shift_id?: string
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  change_amount: number
  status: 'pending' | 'completed' | 'voided' | 'refunded'
  notes?: string
  items: SaleItem[]
  payments: Payment[]
  customer?: { id: string; name: string; phone?: string }
  warehouse: { id: string; name: string; code: string }
  user: { id: string; full_name: string }
  created_at: string
  updated_at: string
  voided_at?: string
  void_reason?: string
}

export interface SaleItem {
  id: string
  product_id: string
  variant_id?: string
  quantity: number
  unit_price: number
  discount_percent: number
  tax_rate: number
  line_total: number
  product: { id: string; sku: string; name: string }
  variant?: { id: string; sku: string; name: string }
}

export interface SaleCreate {
  cart_id: string
  warehouse_id: string
  customer_id?: string
  notes?: string
}

export interface SaleListParams extends PaginationParams {
  date_from?: string
  date_to?: string
  status?: string
  customer_id?: string
  warehouse_id?: string
}

export interface Payment {
  id: string
  sale_id: string
  amount: number
  payment_method: 'cash' | 'card' | 'zarinpal' | 'idpay' | 'nextpay' | 'yekpay' | 'wallet' | 'credit'
  reference?: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  gateway_transaction_id?: string
  processed_at?: string
  created_at: string
}

export interface PaymentCreate {
  amount: number
  payment_method: 'cash' | 'card' | 'zarinpal' | 'idpay' | 'nextpay' | 'yekpay' | 'wallet' | 'credit'
  reference?: string
}

export interface Receipt {
  id: string
  sale_id: string
  receipt_number: string
  format: 'json' | 'html' | 'pdf' | 'text'
  content?: Record<string, any>
  printed_at?: string
  print_count: number
  created_at: string
}

export interface Shift {
  id: string
  shift_number: string
  user_id: string
  warehouse_id: string
  opening_cash: number
  opening_float: number
  closing_cash?: number
  total_sales: number
  total_transactions: number
  cash_sales: number
  card_sales: number
  digital_sales: number
  cash_difference: number
  status: 'open' | 'closed' | 'auditing'
  opened_at: string
  closed_at?: string
  notes?: string
  user: { id: string; full_name: string }
  warehouse: { id: string; name: string; code: string }
}

export interface ShiftListParams extends PaginationParams {
  status?: string
}

export interface ShiftOpen {
  warehouse_id: string
  opening_cash: number
  opening_float: number
  notes?: string
}

export interface ShiftClose {
  closing_cash: number
  notes?: string
}